import { useMemo } from 'react'
import { useSnapshot } from 'valtio'
import sum from 'lodash/sum'
import { ScoringState } from '@app/storage'
import {
  FeedbackPercentages,
  InteractionType,
  PercentagesByCorrect,
  PercentagesByInteraction,
  ResponseByPracticeTestPointValue,
  ResponseByPracticeTestResponseValue,
  ResponseByPracticeTestSeqResponseValue,
  ResponseByPracticeTestValue,
  ResponseByPracticeTestValueType,
  ResponsesByPracticeTestItemResponseDeclaration,
} from '@app/types'
import {
  findCorrectAnswerItem,
  findCorrectAnswerResponse,
  getCountResponsePercentage,
  getSequenceResponsePercentage,
  ReadonlyPracticeTestAssessmentKeyItem,
} from './itemResponsesScoring'

export const getResponsePercentage = (
  response: ResponseByPracticeTestValue,
  responseType: ResponseByPracticeTestValueType,
  correctAnswerResponse: any,
  totalStudentsCount: number,
  totalSlots?: number,
) => {
  if (!correctAnswerResponse) return

  if (responseType === ResponseByPracticeTestValueType.POINT_VALUE) {
    const isCorrect = (response as ResponseByPracticeTestPointValue).points === correctAnswerResponse.possible
    const percentage = getCountResponsePercentage(
      response as ResponseByPracticeTestPointValue,
      totalStudentsCount,
      totalSlots,
    )

    return { isCorrect, percentage }
  }

  if (responseType === ResponseByPracticeTestValueType.SEQ_RESPONSE_VALUE) {
    const isCorrect = correctAnswerResponse.response.includes(
      (response as ResponseByPracticeTestSeqResponseValue).response,
    )
    const percentage = getSequenceResponsePercentage(
      response as ResponseByPracticeTestSeqResponseValue,
      totalStudentsCount,
      totalSlots,
    )

    return { isCorrect, percentage }
  }

  if (responseType === ResponseByPracticeTestValueType.RESPONSE_VALUE) {
    const isCorrect = correctAnswerResponse.response.includes(
      (response as ResponseByPracticeTestResponseValue).response,
    )
    const percentage = getCountResponsePercentage(
      response as ResponseByPracticeTestResponseValue,
      totalStudentsCount,
      totalSlots,
    )

    return { isCorrect, percentage }
  }
}

export const getPercentagesByResponseDeclaration = (
  responseDeclaration: ResponsesByPracticeTestItemResponseDeclaration,
  correctAnswerItem: ReadonlyPracticeTestAssessmentKeyItem,
  totalStudentsCount: number,
) => {
  const correctAnswerResponse = findCorrectAnswerResponse(
    correctAnswerItem,
    responseDeclaration.responseDeclarationId,
  )

  // Determine the total number of answer slots based on the number of correct answers
  const numberOfCorrectAnswers = correctAnswerResponse?.response?.length || 1
  const totalSlots =
    responseDeclaration.responseType === ResponseByPracticeTestValueType.POINT_VALUE
      ? null
      : numberOfCorrectAnswers > 1
      ? totalStudentsCount * numberOfCorrectAnswers
      : undefined

  return responseDeclaration.responses.reduce<PercentagesByInteraction>(
    (acc, response) => {
      const responsePercentage = getResponsePercentage(
        response as ResponseByPracticeTestValue,
        responseDeclaration.responseType,
        correctAnswerResponse,
        totalStudentsCount,
        totalSlots,
      )

      if (!responsePercentage) return acc

      if (responsePercentage.isCorrect) {
        return {
          ...acc,
          correct: [...acc.correct, responsePercentage.percentage],
        }
      }

      return {
        ...acc,
        incorrect: [...acc.incorrect, responsePercentage.percentage],
      }
    },
    { correct: [], incorrect: [] },
  )
}

export const getFeedbackPercentages = (
  percentagesByCorrect: PercentagesByInteraction | PercentagesByCorrect,
  interactionType?: InteractionType,
  responseType?: ResponseByPracticeTestValueType,
): FeedbackPercentages => {
  let correctRaw: number
  let incorrectRaw: number

  if (
    interactionType === InteractionType.choice ||
    responseType === ResponseByPracticeTestValueType.POINT_VALUE
  ) {
    // Round individual percentages before summing to match display logic
    const correctPercentages = percentagesByCorrect.correct.flat().map(p => Math.round(p * 100) / 100)
    const incorrectPercentages = percentagesByCorrect.incorrect.flat().map(p => Math.round(p * 100) / 100)
    correctRaw = correctPercentages.length ? sum(correctPercentages) : 0
    incorrectRaw = incorrectPercentages.length ? sum(incorrectPercentages) : 0
  } else {
    ;[correctRaw, incorrectRaw] = [percentagesByCorrect.correct, percentagesByCorrect.incorrect].map(
      percentages => {
        const rounded = percentages.flat().map(p => Math.round(p * 100) / 100)
        return rounded.length ? sum(rounded) / rounded.length : 0
      },
    )
  }

  // Round correct and incorrect, then calculate noAnswer to ensure they sum to 100%
  const correct = Math.round(correctRaw * 100) / 100
  const incorrect = Math.round(incorrectRaw * 100) / 100
  const noAnswer = Math.max(0, 1 - correct - incorrect)

  return { correct, incorrect, noAnswer }
}

export const useItemResponsePercentages = (itemId: string) => {
  const { groupTestScoring, groupCorrectAnswers, isPreviewMode } = useSnapshot(ScoringState)

  const responsePercentages = useMemo(() => {
    // In preview mode, return null to hide feedback boxes entirely
    if (isPreviewMode) {
      return null
    }

    if (!groupTestScoring || !groupCorrectAnswers) {
      return null
    }

    const scoringItem = groupTestScoring.items.find(item => item.itemId === itemId)
    const correctAnswerItem = findCorrectAnswerItem(groupCorrectAnswers, itemId)

    if (!correctAnswerItem) {
      return
    }

    if (!scoringItem) {
      // If there is no answer on current question, display statistic feedback
      return { correct: 0, incorrect: 0, noAnswer: 1 }
    }

    const percentagesByInteraction = scoringItem.responseDeclarations.map(responseDeclaration =>
      getPercentagesByResponseDeclaration(
        responseDeclaration as ResponsesByPracticeTestItemResponseDeclaration,
        correctAnswerItem,
        groupTestScoring.studentCount,
      ),
    )

    const percentagesByCorrect = percentagesByInteraction.reduce<PercentagesByCorrect>(
      (acc, interactionPercentages) => ({
        correct: [...acc.correct, interactionPercentages.correct],
        incorrect: [...acc.incorrect, interactionPercentages.incorrect],
      }),
      { correct: [], incorrect: [] },
    )

    return getFeedbackPercentages(percentagesByCorrect)
  }, [groupCorrectAnswers, groupTestScoring, itemId, isPreviewMode])

  return responsePercentages
}

export const useResponsePercentages = (
  itemId: string,
  responseDeclarationId: string,
  interactionType: InteractionType,
) => {
  const { groupTestScoring, groupCorrectAnswers } = useSnapshot(ScoringState)

  const responsePercentages = useMemo(() => {
    if (!groupTestScoring || !groupCorrectAnswers) {
      return null
    }

    const scoringResponseDeclaration = groupTestScoring.items
      .find(item => item.itemId === itemId)
      ?.responseDeclarations?.find(
        responseDeclaration => responseDeclaration.responseDeclarationId === responseDeclarationId,
      )
    const correctAnswerItem = findCorrectAnswerItem(groupCorrectAnswers, itemId)

    if (!correctAnswerItem) {
      return
    }

    // If there is no answer on current question, display statistic feedback
    if (!scoringResponseDeclaration) {
      return { correct: 0, incorrect: 0, noAnswer: 1 }
    }

    const percentagesByCorrect = getPercentagesByResponseDeclaration(
      scoringResponseDeclaration as ResponsesByPracticeTestItemResponseDeclaration,
      correctAnswerItem,
      groupTestScoring.studentCount,
    )

    return getFeedbackPercentages(
      percentagesByCorrect,
      interactionType,
      scoringResponseDeclaration?.responseType,
    )
  }, [groupCorrectAnswers, groupTestScoring, itemId, responseDeclarationId, interactionType])

  return responsePercentages
}
